import React, { useState, useRef, memo, useMemo, useCallback } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useSimpleDragManager } from './SimpleDragManager';
import { safeAssemblySwitch, isActivelyWorkingOnAssembly } from '../../utils/assemblyProtection';
import { TakeoffAssembly } from '../../types/AssemblyItemType';
import { useFetchCountedAssemblies } from '../../hooks/useFetchCountedAssemblies';

export type Style = Pick<
  TakeoffAssembly,
  | 'pointtype'
  | 'pointsize'
  | 'pointbordercolor'
  | 'pointfillcolor'
  | 'linedasharray'
  | 'linesize'
  | 'linecolor'
>;

export interface AssemblyStyle extends Style {
  isselected: boolean;
  fillopacity?: number;
  verticallength?: number;
}
export interface PointIndex {
  assemblyIndex: number;
  pointIndex: number;
}

interface SVGShapeNewProps {
  point: { x: number; y: number; pointid?: string };
  pointIndex: number;
  assemblyIndex: number;
  assemblyId: string;
  assemblyStyle: AssemblyStyle;
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
  verticalLength: number;
}

/**
 * Optimized SVGShape component that separates visual drag state from data updates
 * Uses DragManager for efficient drag handling without store mutations during drag
 */
export const SVGShapeNew: React.FC<SVGShapeNewProps> = memo(
  ({
    point,
    pointIndex,
    assemblyIndex,
    assemblyId,
    assemblyStyle,
    cursorPositionRef,
    verticalLength,
  }) => {
    const { setIsEditingPoint, showPointMeasurements, setHasPointEditorInteraction } =
      useStore(useUnityBuildStore);
    const { data: filteredCountedAssemblies } = useFetchCountedAssemblies();
    const { currentTakeoffAssembly, setCurrentTakeoffAssembly } = useStore(useAssemblyStore);
    const { selectedPoints, isPointInSelectedGroup } = useStore(useSelectionStore);
    const { isDragging, startDrag, getPointPosition, isDraggingPoint } = useSimpleDragManager();

    const svgElementRef = useRef<SVGGElement>(null);
    const [isHovered, setIsHovered] = useState(false);

    // Check if this point is selected
    const isSelected = point.pointid ? isPointInSelectedGroup(point.pointid) : false;

    // Get the visual position (either from drag state or actual position)
    // Memoize to reduce calculation overhead
    const visualPosition = useMemo(() => {
      // Use the point coordinates passed in (which include live positions from getEffectivePosition)
      return { x: point.x, y: point.y };
    }, [point.x, point.y]);

    // Determine visual styling based on state
    const fillColor = isSelected
      ? isDragging && isDraggingPoint(point.pointid || '')
        ? '#FF6B35'
        : '#4285F4'
      : assemblyStyle.pointfillcolor;

    const strokeColor = isSelected
      ? isDragging && isDraggingPoint(point.pointid || '')
        ? '#FF6B35'
        : '#4285F4'
      : assemblyStyle.pointbordercolor;

    const fillOpacity = isSelected ? 0.7 : assemblyStyle.fillopacity || 0.5;

    const handleContextMenu = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();

        if (!point?.pointid) return;

        // Set the current assembly and point for editing (current page only)
        const allAssemblies = Array.isArray(filteredCountedAssemblies)
          ? []
          : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
              (group: any) => group.assembliesList || []
            ) || [];
        const assembly = allAssemblies[assemblyIndex];
        if (assembly) {
          setCurrentTakeoffAssembly(assembly);
        }

        // Open the point editor at the mouse position
        // Ensure Takeoff Editor visibility flag in AssemblyStore is set
        // so MasterProjectView condition renders PointEditor
        try {
          // Use store getter to avoid hook usage inside callback
          useAssemblyStore.getState().setIsViewingTakeoffEditor(true);
        } catch {}

        setIsEditingPoint({
          isEditingPoint: true,
          isViewingTakeoffEditor: true,
          x: e.clientX,
          y: e.clientY,
          pointIndex: pointIndex,
          lineIndex: null,
        });
        setHasPointEditorInteraction(false);
        try {
          useUnityBuildStore.getState().setIsAnnotatingTakeoff(true);
        } catch {}
      },
      [
        point?.pointid,
        filteredCountedAssemblies,
        assemblyIndex,
        pointIndex,
        setCurrentTakeoffAssembly,
        setIsEditingPoint,
        setHasPointEditorInteraction,
      ]
    );

    // No need to hide shapes - simple drag system renders live positions directly

    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      if (e.button !== 0 || isDragging) return;

      const allAssemblies = Array.isArray(filteredCountedAssemblies)
        ? []
        : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
            (group: any) => group.assembliesList || []
          ) || [];
      const assembly = allAssemblies[assemblyIndex];
      if (!assembly || !point.pointid) return;

      // Check if we should block interaction with this point
      if (currentTakeoffAssembly && isActivelyWorkingOnAssembly() && !isSelected) {
        // Silently block interaction with points from different assemblies
        // But allow interaction with selected points (for multi-point drag)
        return;
      }

      // Determine drag mode
      if (isSelected && selectedPoints.length > 1) {
        // Start group drag
        const pointsToMove = selectedPoints.map(sp => {
          // Find the actual point data (current page only)
          const allAssemblies = Array.isArray(filteredCountedAssemblies)
            ? []
            : filteredCountedAssemblies?.filteredAssemblies?.flatMap(
                (group: any) => group.assembliesList || []
              ) || [];
          const asm = allAssemblies.find((a: any) => a.assemblyid === sp.assemblyId);
          const pt = asm?.pointsList?.find((p: any) => p.pointid === sp.pointId);

          return {
            pointId: sp.pointId,
            assemblyId: sp.assemblyId,
            x: pt?.x || sp.originalX,
            y: pt?.y || sp.originalY,
          };
        });

        startDrag('group', pointsToMove, cursorPositionRef.current);
      } else {
        // Start single point drag
        startDrag(
          'single',
          [
            {
              pointId: point.pointid,
              assemblyId: assemblyId,
              x: point.x,
              y: point.y,
            },
          ],
          cursorPositionRef.current
        );
      }

      // Use safe assembly switch to protect active work
      // But allow switching if this point is selected (for multi-point operations)
      if (isSelected || !isActivelyWorkingOnAssembly()) {
        safeAssemblySwitch(assemblyId, currentTakeoffAssembly?.assemblyid || null, () =>
          setCurrentTakeoffAssembly(assembly)
        );
      }
    };

    // Global mouse move handler - moved to SVGPointsNew to avoid multiple listeners

    // Render shape based on type
    const renderShape = () => {
      const shapeProps = {
        fill: fillColor,
        stroke: strokeColor,
        fillOpacity: fillOpacity,
        strokeWidth: assemblyStyle.linesize || 1,
        onMouseDown: handleMouseDown,
        onContextMenu: handleContextMenu,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        style: {
          cursor:
            currentTakeoffAssembly && !isDragging
              ? 'crosshair'
              : isDragging
                ? 'grabbing'
                : isHovered
                  ? 'grab'
                  : 'default',
        },
      };

      const size = assemblyStyle.pointsize || 5;

      switch (assemblyStyle.pointtype) {
        case 'circle':
          return <circle cx={visualPosition.x} cy={visualPosition.y} r={size} {...shapeProps} />;

        case 'square':
        case 'rect':
          return (
            <rect
              x={visualPosition.x - size / 2}
              y={visualPosition.y - size / 2}
              width={size}
              height={size}
              {...shapeProps}
            />
          );

        case 'triangle':
          const trianglePoints = `
          ${visualPosition.x},${visualPosition.y - size / 2}
          ${visualPosition.x - size / 2},${visualPosition.y + size / 2}
          ${visualPosition.x + size / 2},${visualPosition.y + size / 2}
        `;
          return <polygon points={trianglePoints} {...shapeProps} />;

        case 'diamond':
          const diamondPoints = `
          ${visualPosition.x},${visualPosition.y - size / 2}
          ${visualPosition.x + size / 2},${visualPosition.y}
          ${visualPosition.x},${visualPosition.y + size / 2}
          ${visualPosition.x - size / 2},${visualPosition.y}
        `;
          return <polygon points={diamondPoints} {...shapeProps} />;

        case 'cross':
          const strokeWidth = Math.max(1, Math.round(size / 6));
          return (
            <g {...shapeProps}>
              <line
                x1={visualPosition.x - size / 2}
                y1={visualPosition.y - size / 2}
                x2={visualPosition.x + size / 2}
                y2={visualPosition.y + size / 2}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
              />
              <line
                x1={visualPosition.x - size / 2}
                y1={visualPosition.y + size / 2}
                x2={visualPosition.x + size / 2}
                y2={visualPosition.y - size / 2}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
              />
            </g>
          );

        case 'plus':
          const plusStrokeWidth = Math.max(1, Math.round(size / 6));
          return (
            <g {...shapeProps}>
              <line
                x1={visualPosition.x - size / 2}
                y1={visualPosition.y}
                x2={visualPosition.x + size / 2}
                y2={visualPosition.y}
                stroke={strokeColor}
                strokeWidth={plusStrokeWidth}
              />
              <line
                x1={visualPosition.x}
                y1={visualPosition.y - size / 2}
                x2={visualPosition.x}
                y2={visualPosition.y + size / 2}
                stroke={strokeColor}
                strokeWidth={plusStrokeWidth}
              />
            </g>
          );

        case 'star':
          const rOuter = size / 2;
          const rInner = rOuter / 2.5;
          const starPoints: string[] = [];
          for (let i = 0; i < 10; i++) {
            const angle = (Math.PI / 5) * i - Math.PI / 2;
            const r = i % 2 === 0 ? rOuter : rInner;
            starPoints.push(
              `${visualPosition.x + r * Math.cos(angle)},${visualPosition.y + r * Math.sin(angle)}`
            );
          }
          return <polygon points={starPoints.join(' ')} {...shapeProps} />;

        case 'hexagon':
          const hexRadius = size / 2;
          const hexPoints: string[] = [];
          for (let i = 0; i < 6; i++) {
            const angle = (Math.PI / 3) * i - Math.PI / 2;
            hexPoints.push(
              `${visualPosition.x + hexRadius * Math.cos(angle)},${visualPosition.y + hexRadius * Math.sin(angle)}`
            );
          }
          return <polygon points={hexPoints.join(' ')} {...shapeProps} />;

        default:
          return <circle cx={visualPosition.x} cy={visualPosition.y} r={size} {...shapeProps} />;
      }
    };

    return (
      <g ref={svgElementRef}>
        {/* Selection indicator */}
        {isSelected && (
          <circle
            cx={visualPosition.x}
            cy={visualPosition.y}
            r={(assemblyStyle.pointsize || 5) + 3}
            fill="none"
            stroke={isDragging && isDraggingPoint(point.pointid || '') ? '#FF6B35' : '#4285F4'}
            strokeWidth={1}
            strokeDasharray="4,2"
            pointerEvents="none"
          />
        )}

        {/* Main shape */}
        {renderShape()}

        {/* Professional vertical length label */}
        {showPointMeasurements && verticalLength > 0 && (
          <>
            {/* Measurement background - smaller box for point measurements */}
            <rect
              x={visualPosition.x + 2 - 12}
              y={visualPosition.y + 16 - 7}
              width="24"
              height="12"
              fill="white"
              fillOpacity={0.95}
              stroke="#ccc"
              strokeWidth={0.3}
              rx={1}
              pointerEvents="none"
            />
            {/* Measurement text */}
            <text
              x={visualPosition.x + 2}
              y={visualPosition.y + 16}
              fill="#333"
              fontSize="9"
              fontWeight="400"
              textAnchor="middle"
              fontFamily="Arial, sans-serif"
              pointerEvents="none"
            >
              {`${parseFloat(verticalLength.toFixed(2))}'`}
            </text>
          </>
        )}
      </g>
    );
  }
);
